<script setup lang="ts">
/** Who signs, and what they are signing. */
import { useI18n } from 'vue-i18n'

import InspectorField from '@/sections/InspectorField.vue'
import { newSignatory, type Signatory, type SignaturesSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: SignaturesSection }>()
const emit = defineEmits<{ change: [section: SignaturesSection] }>()

function set(changes: Partial<SignaturesSection>): void {
  emit('change', { ...props.section, ...changes })
}

function setPerson(id: string, changes: Partial<Signatory>): void {
  set({
    signatories: props.section.signatories.map((person) =>
      person.id === id ? { ...person, ...changes } : person,
    ),
  })
}

function add(): void {
  set({ signatories: [...props.section.signatories, newSignatory()] })
}

function remove(id: string): void {
  set({ signatories: props.section.signatories.filter((person) => person.id !== id) })
}
</script>

<template>
  <div class="fields">
    <InspectorField
      :label="t('inspector.signatures.statement')"
      :hint="t('inspector.signatures.statementHint')"
    >
      <textarea
        class="b-textarea"
        :value="section.statement"
        :placeholder="t('inspector.signatures.statementPlaceholder')"
        @input="set({ statement: ($event.target as HTMLTextAreaElement).value })"
      />
    </InspectorField>

    <div class="people">
      <article v-for="person in section.signatories" :key="person.id" class="person">
        <header class="person__head">
          <input
            class="b-input"
            :value="person.name"
            :placeholder="t('inspector.signatures.namePlaceholder')"
            @input="setPerson(person.id, { name: ($event.target as HTMLInputElement).value })"
          />
          <button
            type="button"
            class="b-icon-btn"
            :aria-label="t('inspector.signatures.removeSignatory')"
            @click="remove(person.id)"
          >
            &times;
          </button>
        </header>
        <input
          class="b-input"
          :value="person.role"
          :placeholder="t('inspector.signatures.rolePlaceholder')"
          @input="setPerson(person.id, { role: ($event.target as HTMLInputElement).value })"
        />
        <input
          class="b-input"
          :value="person.place"
          :placeholder="t('inspector.signatures.placePlaceholder')"
          @input="setPerson(person.id, { place: ($event.target as HTMLInputElement).value })"
        />
      </article>
    </div>

    <button type="button" class="b-btn b-btn--outline" @click="add">
      {{ t('inspector.signatures.addSignatory') }}
    </button>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.people {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.person {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-5);
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.person__head {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
}
</style>
